import { ecId, type Row } from '../ability/context';
import { statusRows, statusItemId, statusCasterId, STATUS_IDS } from '../statuses/expiry';
import { statusScalar } from '../actions/activity';
import { fromBaseUuid } from '../compile/from-base-context';

const value = (row: Row, key: string): string => String(statusScalar(row, key) ?? '');
const uuid = (row: Row, key: string): string => value(row, key).toUpperCase();

/** All projections refer to one physical row. Target lists take precedence over caster copies. */
export function deathStatusRows(holders: readonly Row[]): Row[] {
    const rows = new Map<string, Row>();
    for (const side of ['target', 'caster'] as const) for (const holder of holders) for (const status of statusRows(holder, side)) {
        const id = uuid(status, 'status_id');
        if (fromBaseUuid(id) && !rows.has(id)) rows.set(id, status);
    }
    return [...rows.values()].filter(status => Number(statusScalar(status, 'status_deleted') ?? 0) === 0);
}

/** The removal executor owns loss hooks, linked specials and the live projections. It is called
 * sequentially: a previous removal can retire a later candidate or a concentration source. */
export interface DeathStatusExecutor {
    rows(): readonly Row[];
    /** Includes unsaved rows from earlier effects in this same action. */
    targetStatuses?(targetId: string): readonly Row[];
    removeTarget(targetId: string, statusId: string): void;
    removeConnection(targetId: string, statusId: string): void;
    /** Native fallback for a caster projection whose target is outside the hydrated roster. */
    retire(status: Row): void;
    /** Remove the dying grappler's stale local copy after the victim's linked teardown. */
    forgetTargetStatus(targetId: string, statusId: string): void;
    concentrationBroken(targetId: string): void;
}

/** Release caster-death effects and the dying caster's concentration sources. Hydrated
 * targets run the removal hooks; unhydrated targets retire every physical connection row. */
export function releaseDyingCasterStatuses(dying: Row, holders: readonly Row[], whitelist: readonly string[], execute: DeathStatusExecutor): void {
    const id = ecId(dying), allowed = new Set(whitelist.map(id => id.toUpperCase()));
    if (!fromBaseUuid(id)) return;
    const held = new Map(holders.map(row => [ecId(row), row]));
    const candidates = execute.rows().filter(status => statusCasterId(status) === id);
    for (const candidate of candidates) {
        const status = execute.rows().find(row => uuid(row, 'status_id') === uuid(candidate, 'status_id'));
        if (!status || !allowed.has(statusItemId(status))) continue;
        const target = uuid(status, 'status_target_id'), statusId = uuid(status, 'status_id');
        if (!fromBaseUuid(target) || target === id || !fromBaseUuid(statusId)) continue;
        const member = held.get(target);
        if (member && (execute.targetStatuses?.(target) ?? statusRows(member, 'target')).length) execute.removeTarget(target, statusId);
        else {
            execute.retire(status);
            const connection = uuid(status, 'status_connected_id');
            if (fromBaseUuid(connection)) for (const peer of execute.rows())
                if (uuid(peer, 'status_connected_id') === connection && uuid(peer, 'status_id') !== statusId) execute.retire(peer);
        }
    }
    for (const status of [...(execute.targetStatuses?.(id) ?? statusRows(dying, 'target'))])
        if (statusItemId(status) === STATUS_IDS.concentrating) execute.removeConnection(id, uuid(status, 'status_id'));
}

/** Snapshot before stripping target statuses. Historical projections include tags already
 * detached by a source removal; pending rows can also come from the action's live ledger. */
export function dyingStatusConnections(dying: Row, relatedRows: readonly Row[]): Map<string, string> {
    const result = new Map<string, string>();
    for (const status of [...statusRows(dying, 'target'), ...relatedRows]) {
        if (uuid(status, 'status_target_id') !== ecId(dying)) continue;
        const connection = uuid(status, 'status_connected_id'), caster = statusCasterId(status);
        if (fromBaseUuid(connection) && fromBaseUuid(caster) && !result.has(connection)) result.set(connection, caster);
    }
    return result;
}

/** Any other live row, including a buff or a sibling
 * tag, preserves concentration. A caster can have more than one Concentrating row on the cast. */
export function endOrphanedConcentration(connections: ReadonlyMap<string, string>, execute: DeathStatusExecutor): void {
    for (const [connection, caster] of connections) {
        const live = execute.rows().filter(row => uuid(row, 'status_connected_id') === connection);
        if (!live.length || live.some(row => statusItemId(row) !== STATUS_IDS.concentrating || uuid(row, 'status_target_id') !== caster)) continue;
        for (const status of live) execute.removeConnection(caster, uuid(status, 'status_id'));
        execute.concentrationBroken(caster);
    }
}

/** release_grapples_owned_by_dying_grappler: prefer the victim's exact Grappled row; otherwise
 * remove the grappler's row so the ordinary counterpart teardown can find the remote victim. */
export function releaseDyingGrapples(dying: Row, members: readonly Row[], execute: DeathStatusExecutor): void {
    const grappler = ecId(dying);
    const targetStatuses = (member: Row): readonly Row[] => execute.targetStatuses?.(ecId(member)) ?? statusRows(member, 'target');
    for (const status of [...targetStatuses(dying)]) {
        if (statusItemId(status) !== STATUS_IDS.grappling) continue;
        const statusId = uuid(status, 'status_id'), connection = uuid(status, 'status_connected_id');
        if (!execute.rows().some(row => uuid(row, 'status_id') === statusId)) continue;
        let victim = [uuid(status, 'status_target_id'), statusCasterId(status)].find(id => fromBaseUuid(id) && id !== grappler) ?? '';
        if (!victim) {
            const heldVictim = members.find(member => ecId(member) !== grappler && targetStatuses(member)
                .some(row => statusItemId(row) === STATUS_IDS.grappled && statusCasterId(row) === grappler));
            if (heldVictim) victim = ecId(heldVictim);
        }
        const member = members.find(row => ecId(row) === victim);
        const grip = member && targetStatuses(member).find(row => statusItemId(row) === STATUS_IDS.grappled
            && (statusCasterId(row) === grappler || (connection !== '' && uuid(row, 'status_connected_id') === connection))
            && fromBaseUuid(uuid(row, 'status_id')));
        if (grip) {
            execute.removeTarget(victim, uuid(grip, 'status_id'));
            execute.forgetTargetStatus(grappler, statusId);
        }
        else if (fromBaseUuid(statusId)) execute.removeTarget(grappler, statusId);
    }
}

/** Complete death status phase, before corpse posture / drops. Settle sacrifice while the
 * marks are live, then release concentration only when no beneficiary survives the cleanup. */
export function resolveDeathStatusCleanup(dying: Row, holders: readonly Row[], whitelist: readonly string[],
    historicalTags: readonly Row[], execute: DeathStatusExecutor, sacrifice: (dying: Row) => void): void {
    const connections = dyingStatusConnections(dying, [...historicalTags, ...execute.rows()]);
    const statuses = (): readonly Row[] => execute.targetStatuses?.(ecId(dying)) ?? statusRows(dying, 'target');
    releaseDyingGrapples(dying, holders, execute);
    releaseDyingCasterStatuses(dying, holders, whitelist, execute);
    if (statuses().length) {
        sacrifice(dying);
        for (const status of [...statuses()]) {
            if (statusItemId(status) === STATUS_IDS.grappled || statusItemId(status) === 'F5ACBDB4-D923-3684-57F6-FBF3F720AEFA') continue;
            execute.removeTarget(ecId(dying), uuid(status, 'status_id'));
        }
    }
    endOrphanedConcentration(connections, execute);
}
